import { Group, Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { SourceTypeFilter } from '@/module/Author/Publication/filters/SourceTypeFilter';
import { PublisherTypeFilter } from '@/module/Nomen/PublisherType/PublisherTypeFilter';

import { AddButtonLink, Browse, NumberFilter, useFilter, useNumberFilter, usePagination } from '@/core/components';

import { FilterBox, SearchFilter, useSearch, useSortDirection } from '@feature/utils';

import { TranslationCard } from './components/Card/TranslationCard';
import { getTranslationPagedOptions } from './model/TranslationQuery';

export function TranslationBrowse() {
  const { t } = useTranslation();
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { search, searchProps } = useSearch();
  const { pagination, useTotalElements, ...paginationProps } = usePagination({ search });
  const { id: publisherTypeId, ...typeFilterProps } = useFilter();
  const { id: sourceType, ...sourceTypeFilterProps } = useFilter();
  const { item: publishYear, ...publishYearFilterProps } = useNumberFilter();
  const { item: indexingYear, ...indexingYearFilterProps } = useNumberFilter();

  const { data } = useSuspenseQuery(
    getTranslationPagedOptions({
      ...pagination,
      sortDirection,
      search,
      sourceType,
      publisherTypeId,
      publishYear,
      indexingYear,
    }),
  );
  useTotalElements(data);

  return (
    <Browse
      actions={<AddButtonLink to="search-claimable" />}
      title={t('translations.title')}
      subtitle={t('translations.subtitle')}
      pagination={paginationProps}
      filters={
        <FilterBox>
          <Stack>
            <SearchFilter
              label={t('label.search')}
              description={t('translations.searchBy')}
              {...searchProps}
            />
            <Group>
              <NumberFilter
                label={t('translations.filters.publishYear')}
                allowDecimal={false}
                min={1997}
                max={new Date().getFullYear()}
                {...publishYearFilterProps}
                flex={0.25}
              />
              <NumberFilter
                label={t('translations.filters.indexingYear')}
                allowDecimal={false}
                min={1997}
                max={new Date().getFullYear()}
                {...indexingYearFilterProps}
                flex={0.25}
              />
              <PublisherTypeFilter
                label={t('texts.form.fields.publisherType')}
                {...typeFilterProps}
                flex={0.25}
              />
              <SourceTypeFilter
                label={t('publications.filters.sourceType')}
                {...sourceTypeFilterProps}
                flex={0.25}
              />
            </Group>
          </Stack>
        </FilterBox>
      }
      sortDirection={sortDirection}
      toggleSortDirection={toggleSortDirection}
      sortedBy={t('translations.sortedByPublishDate')}
      data={data.list}
      renderItem={translation => (
        <TranslationCard
          key={translation.id}
          translation={translation}
          to={`${translation.id}`}
        />
      )}
    />
  );
}
